import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { CONTRACTS, ABIS, ILK_WPLS } from '../utils/contracts';
import { txOverrides } from '../utils/tx';
import {
  WAD, RAY, RAD,
  oraclePlsPriceWad, jdaiTargetWad, matToWad, wdiv,
  healthRatioWad, maxDebtWad, liquidationPriceWad, debtWadFromArt, wadToNumber
} from '../utils/vaultMath';

// ---------------------------------------------------------------------------
// Easy Vault: WPLS-A positions held as numbered CDPs through MakerDAO's audited
// proxy stack. Every operation is ONE transaction — DssProxyActions wraps native
// PLS to WPLS (and back) internally, so the user only ever handles PLS. The one
// prerequisite is a personal DSProxy (one-time build via the ProxyRegistry).
// ---------------------------------------------------------------------------

export const useEasyVault = (signer, account, onAfterTx) => {
  const [state, setState] = useState({
    proxy: null,          // DSProxy address or null (needs one-time build)
    cdpId: null,          // first WPLS-A cdp id (V1: one vault per user)
    urn: null,
    collateral: '0',      // PLS (as WPLS) locked, WAD string
    debt: '0',            // true debt (art * rate)
    healthRatio: 0,       // normalized: 1.0 = at the 150% line
    liquidationPrice: '0',
    maxDebt: '0',
    availableDebt: '0',   // WPLS-A ceiling room
    minDebt: '1',
    rawIlk: null,         // { rate, spot, line, dust } BigInts for goal-seek math
    allowanceOk: false,   // JDAI allowance user -> proxy covers full debt
    isLoading: true
  });

  const refresh = useCallback(async () => {
    if (!signer || !account) { setState((p) => ({ ...p, isLoading: false })); return; }
    try {
      const provider = signer.provider;
      const registry = new ethers.Contract(CONTRACTS.PROXY_REGISTRY, ABIS.PROXY_REGISTRY, provider);
      const proxyAddr = await registry.proxies(account);
      const proxy = proxyAddr === ethers.ZeroAddress ? null : proxyAddr;

      const vat = new ethers.Contract(CONTRACTS.VAT, ABIS.VAT, provider);
      const spotter = new ethers.Contract(CONTRACTS.SPOTTER, ABIS.SPOTTER, provider);
      const [Art, rate, spot, line, dust] = await vat.ilks(ILK_WPLS);
      const [, mat] = await spotter.ilks(ILK_WPLS);
      const par = await spotter.par();

      // Pick the first WPLS-A cdp that holds an ACTIVE position (collateral or
      // debt). A previously-liquidated cdp is left empty but still exists — and
      // reopening it in one shot via lockETHAndDraw reverts on its residual
      // dust state, so treat empty cdps as "no vault" and open a fresh one.
      let cdpId = null, urn = null, ink = 0n, art = 0n;
      if (proxy) {
        const getCdps = new ethers.Contract(CONTRACTS.GET_CDPS, ABIS.GET_CDPS, provider);
        const [ids, urns, ilks] = await getCdps.getCdpsAsc(CONTRACTS.CDP_MANAGER, proxy);
        for (let i = 0; i < ids.length; i++) {
          if (ilks[i] !== ILK_WPLS) continue;
          const [ink_i, art_i] = await vat.urns(ILK_WPLS, urns[i]);
          if (ink_i > 0n || art_i > 0n) { cdpId = ids[i]; urn = urns[i]; ink = ink_i; art = art_i; break; }
        }
      }

      const priceWad = oraclePlsPriceWad(spot, mat, par);
      const targetWad = jdaiTargetWad(par);
      const debtWad = debtWadFromArt(art, rate);
      const healthWad = healthRatioWad(ink, priceWad, debtWad, targetWad);
      const matWad = matToWad(mat);
      const liqWad = ink > 0n && debtWad > 0n
        ? liquidationPriceWad(ink, debtWad, targetWad, mat)
        : (mat > 0n ? (priceWad * RAY) / mat : 0n);

      // Repay allowance: user must approve their OWN proxy once; check against the
      // full tab plus headroom so "repay all" never fails a wei short.
      let allowanceOk = false;
      if (proxy && debtWad > 0n) {
        const jdai = new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, provider);
        allowanceOk = (await jdai.allowance(account, proxy)) >= debtWad + WAD;
      }

      const ceiling = wadToNumber((line * WAD) / RAD);
      const minted = wadToNumber(debtWadFromArt(Art, rate));

      setState({
        proxy,
        cdpId: cdpId !== null ? cdpId.toString() : null,
        urn,
        collateral: ethers.formatEther(ink),
        debt: ethers.formatEther(debtWad),
        healthRatio: healthWad === null ? Infinity : wadToNumber(wdiv(healthWad, matWad)),
        liquidationPrice: wadToNumber(liqWad).toFixed(8),
        maxDebt: wadToNumber(maxDebtWad(ink, priceWad, mat, targetWad)).toString(),
        availableDebt: Math.max(0, ceiling - minted).toString(),
        minDebt: wadToNumber((dust * WAD) / RAD).toString(),
        rawIlk: { ink, art, rate, spot, line, dust, mat, par },
        allowanceOk,
        isLoading: false
      });
    } catch (e) {
      console.error('Easy vault refresh failed:', e);
      setState((p) => ({ ...p, isLoading: false }));
    }
  }, [signer, account]);

  useEffect(() => { refresh(); }, [refresh]);

  // --- actions (each = one transaction; two-phase feedback is the caller's job) ---

  const send = async (txPromise) => {
    const tx = await txPromise;
    await tx.wait();
    await refresh();
    if (onAfterTx) onAfterTx();
    return tx;
  };

  const buildProxy = async () => {
    const registry = new ethers.Contract(CONTRACTS.PROXY_REGISTRY, ABIS.PROXY_REGISTRY, signer);
    return send(registry.build(await txOverrides(signer.provider)));
  };

  const execute = async (fnName, args, value = 0n) => {
    const proxy = new ethers.Contract(state.proxy, ABIS.DS_PROXY, signer);
    const actions = new ethers.Interface(ABIS.PROXY_ACTIONS);
    const data = actions.encodeFunctionData(fnName, args);
    const ov = await txOverrides(signer.provider);
    return send(proxy.execute(CONTRACTS.PROXY_ACTIONS, data, { ...ov, value }));
  };

  /** Deposit `plsWad` native PLS and mint `jdaiWad` — opens the cdp if none exists. */
  const borrow = (plsWad, jdaiWad) =>
    state.cdpId
      ? execute('lockETHAndDraw',
          [CONTRACTS.CDP_MANAGER, CONTRACTS.JUG, CONTRACTS.GEMJOIN_WPLS_A, CONTRACTS.DAIJOIN, state.cdpId, jdaiWad], plsWad)
      : execute('openLockETHAndDraw',
          [CONTRACTS.CDP_MANAGER, CONTRACTS.JUG, CONTRACTS.GEMJOIN_WPLS_A, CONTRACTS.DAIJOIN, ILK_WPLS, jdaiWad], plsWad);

  /** One-time infinite JDAI approval to the user's own proxy (repays need it). */
  const approveRepay = async () => {
    const jdai = new ethers.Contract(CONTRACTS.JDAI, ABIS.JDAI, signer);
    return send(jdai.approve(state.proxy, ethers.MaxUint256, await txOverrides(signer.provider)));
  };

  /** Repay `jdaiWad` of debt (dust-guarding is the caller's job). */
  const repay = (jdaiWad) =>
    execute('wipe', [CONTRACTS.CDP_MANAGER, CONTRACTS.DAIJOIN, state.cdpId, jdaiWad]);

  /** Repay the entire debt, leave collateral in place. */
  const repayAll = () =>
    execute('wipeAll', [CONTRACTS.CDP_MANAGER, CONTRACTS.DAIJOIN, state.cdpId]);

  /** Repay everything AND withdraw all collateral as native PLS — closes the position. */
  const closeVault = () =>
    execute('wipeAllAndFreeETH',
      [CONTRACTS.CDP_MANAGER, CONTRACTS.GEMJOIN_WPLS_A, CONTRACTS.DAIJOIN, state.cdpId, state.rawIlk.ink]);

  /** Add collateral only. */
  const addCollateral = (plsWad) =>
    execute('lockETH', [CONTRACTS.CDP_MANAGER, CONTRACTS.GEMJOIN_WPLS_A, state.cdpId], plsWad);

  /** Withdraw collateral only, as native PLS (safety cap is the caller's job). */
  const withdrawCollateral = (plsWad) =>
    execute('freeETH', [CONTRACTS.CDP_MANAGER, CONTRACTS.GEMJOIN_WPLS_A, state.cdpId, plsWad]);

  return { ...state, refresh, buildProxy, borrow, approveRepay, repay, repayAll, closeVault, addCollateral, withdrawCollateral };
};
